/**
 * Normalizes phone number by removing formatting characters
 * while preserving leading + for international numbers.
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  const trimmed = phone.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');

  if (!digits) {
    return hasPlus ? '+' : '';
  }

  return hasPlus ? `+${digits}` : digits;
}

/**
 * Cache lookup keys for a phone — indexes/stores aliases so Webex Contacts and
 * call history (Janus) match despite + prefix and US 10-digit vs +1 E.164 differences.
 *
 * Example: contact `7777999990` ↔ call history `+17777999990`
 */
export function getPhoneLookupKeys(phone: string | null | undefined): string[] {
  const normalized = normalizePhone(phone);
  if (!normalized) {
    return [];
  }

  const keys = new Set<string>();
  const add = (key: string) => {
    if (key) {
      keys.add(key);
    }
  };

  add(normalized);

  const digits = normalized.replace(/\D/g, '');

  if (normalized.startsWith('+')) {
    add(normalized.slice(1));
  } else {
    add(`+${normalized}`);
  }

  // US: 10-digit local (7777999990) ↔ +1 E.164 (+17777999990) ↔ 11-digit (17777999990)
  if (digits.length === 11 && digits.startsWith('1')) {
    const local10 = digits.slice(1);
    add(local10);
    add(`+1${local10}`);
    add(`1${local10}`);
  } else if (digits.length === 10) {
    add(`+1${digits}`);
    add(`1${digits}`);
  }

  return Array.from(keys);
}

/**
 * Masks phone number for privacy-safe logging.
 * Shows only last 4 digits; rest replaced with asterisks.
 */
export function maskPhone(phone: string | null | undefined): string {
  const normalized = normalizePhone(phone);

  if (!normalized || normalized.length <= 4) {
    return '****';
  }

  const last4 = normalized.slice(-4);
  return `****${last4}`;
}

/**
 * Validates if phone number is reasonable for enrichment lookup.
 * Rejects extremely long or short numbers.
 */
export function isValidForLookup(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  const normalized = normalizePhone(phone);
  const digitsOnly = normalized.replace(/\+/g, '');

  return digitsOnly.length >= 7 && digitsOnly.length <= 30;
}
